import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Volume2, RotateCcw, ChevronLeft, ChevronRight, Shuffle } from 'lucide-react';
import { whWordsData } from '@/data/whWordsData';

interface VisualFlashcardsProps {
  currentLanguage: 'english' | 'french';
  speakText: (text: string, lang?: 'en-US' | 'fr-FR') => void;
}

const VisualFlashcards: React.FC<VisualFlashcardsProps> = ({ currentLanguage, speakText }) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [shuffledCards, setShuffledCards] = useState(whWordsData);

  const currentCard = shuffledCards[currentCardIndex];

  const nextCard = () => {
    setCurrentCardIndex((prev) => (prev + 1) % shuffledCards.length);
    setIsFlipped(false);
  };

  const prevCard = () => {
    setCurrentCardIndex((prev) => (prev - 1 + shuffledCards.length) % shuffledCards.length);
    setIsFlipped(false);
  };

  const flipCard = () => {
    setIsFlipped(!isFlipped);
  };

  const shuffleCards = () => {
    const shuffled = [...whWordsData].sort(() => Math.random() - 0.5);
    setShuffledCards(shuffled);
    setCurrentCardIndex(0);
    setIsFlipped(false);
  };

  const resetCards = () => {
    setShuffledCards(whWordsData);
    setCurrentCardIndex(0);
    setIsFlipped(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-primary mb-4">
          {currentLanguage === 'english' ? 'Visual Flashcards' : 'Cartes Visuelles'}
        </h2>
        <p className="text-muted-foreground mb-6">
          {currentLanguage === 'english' 
            ? 'Click the card to flip between English and French. Use the controls to navigate.'
            : 'Cliquez sur la carte pour basculer entre l\'anglais et le français. Utilisez les contrôles pour naviguer.'}
        </p>
        
        {/* Controls */}
        <div className="flex justify-center gap-4 mb-6">
          <Button onClick={shuffleCards} variant="outline" className="flex items-center gap-2">
            <Shuffle className="w-4 h-4" />
            {currentLanguage === 'english' ? 'Shuffle' : 'Mélanger'}
          </Button>
          <Button onClick={resetCards} variant="outline" className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4" />
            {currentLanguage === 'english' ? 'Reset' : 'Réinitialiser'}
          </Button>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center items-center gap-2 mb-6">
          <Badge variant="secondary">
            {currentCardIndex + 1} / {shuffledCards.length}
          </Badge>
        </div>
      </div>

      {/* Flashcard Container */}
      <div className="relative h-96 mb-8">
        <div 
          className="absolute inset-0 cursor-pointer perspective-1000"
          onClick={flipCard}
        >
          <div className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}>
            {/* Front Side - English */}
            <Card className="absolute inset-0 backface-hidden border-4 border-primary/20 hover:border-primary/50 transition-all duration-300">
              <CardContent className="flex flex-col items-center justify-center h-full p-8 bg-gradient-to-br from-primary/5 to-primary/10">
                <div className="text-center space-y-6">
                  <Badge variant="default" className="mb-4">
                    {currentLanguage === 'english' ? 'English' : 'Anglais'}
                  </Badge>
                  
                  <h3 className="text-6xl font-bold text-primary mb-4">
                    {currentCard.word}
                  </h3>
                  
                  <p className="text-xl text-muted-foreground font-mono">
                    {currentCard.pronunciation}
                  </p>
                  
                  <p className="text-lg text-muted-foreground">
                    {currentCard.partOfSpeech}
                  </p>
                  
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      speakText(currentCard.word);
                    }}
                    className="audio-button mt-4"
                    size="lg"
                  >
                    <Volume2 className="w-5 h-5 mr-2" />
                    {currentLanguage === 'english' ? 'Listen' : 'Écouter'}
                  </Button>
                  
                  <p className="text-sm text-muted-foreground mt-4">
                    {currentLanguage === 'english' ? 'Click to see French' : 'Cliquez pour voir le français'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Back Side - French */}
            <Card className="absolute inset-0 backface-hidden rotate-y-180 border-4 border-accent/20 hover:border-accent/50 transition-all duration-300">
              <CardContent className="flex flex-col items-center justify-center h-full p-8 bg-gradient-to-br from-accent/5 to-accent/10">
                <div className="text-center space-y-6">
                  <Badge variant="default" className="mb-4 bg-accent">
                    {currentLanguage === 'english' ? 'French' : 'Français'}
                  </Badge>
                  
                  <h3 className="text-6xl font-bold text-accent mb-4">
                    {currentCard.french}
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="bg-white/50 rounded-lg p-4">
                      <p className="text-lg font-medium text-accent mb-2">
                        {currentLanguage === 'english' ? 'Example:' : 'Exemple:'}
                      </p>
                      <p className="text-accent">
                        {currentCard.examples[0].french}
                      </p>
                    </div>
                  </div>
                  
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      speakText(currentCard.french, 'fr-FR');
                    }}
                    className="audio-button mt-4 bg-accent hover:bg-accent/90"
                    size="lg"
                  >
                    <Volume2 className="w-5 h-5 mr-2" />
                    {currentLanguage === 'english' ? 'Listen' : 'Écouter'}
                  </Button>
                  
                  <p className="text-sm text-muted-foreground mt-4">
                    {currentLanguage === 'english' ? 'Click to see English' : 'Cliquez pour voir l\'anglais'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="flex justify-center items-center gap-6">
        <Button
          onClick={prevCard}
          variant="outline"
          size="lg"
          className="flex items-center gap-2"
          disabled={shuffledCards.length <= 1}
        >
          <ChevronLeft className="w-5 h-5" />
          {currentLanguage === 'english' ? 'Previous' : 'Précédent'}
        </Button>
        
        <Button
          onClick={flipCard}
          variant="default"
          size="lg"
          className="flex items-center gap-2 px-8"
        >
          <RotateCcw className="w-5 h-5" />
          {currentLanguage === 'english' ? 'Flip Card' : 'Retourner'}
        </Button>
        
        <Button
          onClick={nextCard}
          variant="outline"
          size="lg"
          className="flex items-center gap-2"
          disabled={shuffledCards.length <= 1}
        >
          {currentLanguage === 'english' ? 'Next' : 'Suivant'}
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Card Grid Preview */}
      <div className="mt-12">
        <h3 className="text-xl font-semibold text-center mb-6">
          {currentLanguage === 'english' ? 'All Cards' : 'Toutes les Cartes'}
        </h3>
        <div className="grid grid-cols-7 gap-2 max-w-2xl mx-auto">
          {shuffledCards.map((card, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentCardIndex(index);
                setIsFlipped(false);
              }}
              className={`p-2 rounded border text-sm font-medium transition-all ${
                index === currentCardIndex
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-secondary hover:bg-secondary/80 border-border'
              }`}
            >
              {card.word}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VisualFlashcards;